import { useState, useEffect, useMemo } from "react";
import { createUseStyles } from "react-jss";
import LinearProgress from "@mui/material/LinearProgress";
import dateFormat from "dateformat";
import { useParams } from "react-router-dom";
import { theme } from "$utils/theme/bataillog";
import { Text } from "$components";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import { PreparationOrderStatus } from "$types";

type Props = {
  ops: any[];
  step: number;
  label: string;
  currentEdbStep: number;
  isRed: boolean;
  edb?: any;
  hasProduct?: boolean;
};

function Step({
  ops = [],
  step,
  label,
  currentEdbStep = 0,
  isRed = false,
  edb,
  hasProduct,
}: Props) {
  const { idEdb } = useParams<any>();
  const classes = useStyles();
  const [progressBar, setProgressBar] = useState(0);

  const activeOps = useMemo(
    () =>
      (ops || []).filter(
        (op: any) => op.status !== PreparationOrderStatus.CANCELLED
      ),
    [ops]
  );

  const ProductLeft: string[] = [];
  if (!idEdb) {
    let opsProducts: any[] = [];
    const edbProducts: any[] = (edb.products || []).map((p: any) => ({
      id: p.relatedProduct?._id,
      qty: p.qty,
    }));
    (activeOps || []).forEach((op: any) => {
      opsProducts = [
        ...opsProducts,
        ...(op.products || []).map((p: any) => ({
          id: p.relatedProduct?._id,
          qty: p.qty,
        })),
      ];
    });
    (edbProducts || []).forEach((p: any) => {
      const prod = opsProducts.find((x) => x.id === p.id);
      if (prod) {
        // found
        // calculate total qty of this products in all edb
        const qty = opsProducts
          .filter((x: any) => x.id === p.id)
          .map((x: any) => x.qty)
          .reduce((a: number, b: number) => b + a, 0);
        if (qty < p.qty) {
          // if total ops qty is less that edb product asked qty then there are some of this product left
          ProductLeft.push(p);
        }
      } else {
        // if product is not yet in defined OP, then there are some products left
        ProductLeft.push(p);
      }
    });
  }
  const hasLeftProduct = !!ProductLeft.length;

  const getTimeLine = () => {
    const opsModified = (activeOps || []).map((op: any) => ({
      ...op,
      statusHistory: (op?.statusHistory || []).map((h: any) => h.status),
    }));
    const steps = [-1, 0, 1, 2, 3, 4, 5];
    const list = (opsModified || []).map((x: any) => x.statusHistory);
    const fixed = list.map((x) => Array.from(new Set(x)));
    const status = currentEdbStep;
    const timeline = steps.map((step) => {
      if (step < 0 || (currentEdbStep === 0 && step === 0)) {
        return {
          isActive: true,
          isSemi: false,
          step,
        };
      }
      const isActive = currentEdbStep === -1 ? false : step <= status;

      if (step === 0) {
        return {
          isActive,
          semi: false,
          step,
        };
      }

      if (status === 6 && step === 5) {
        return {
          isActive,
          isSemi:
            (hasProduct || hasLeftProduct) && isActive
              ? true
              : isActive
              ? !fixed.every((z) => (z[z.length - 1] as number) === status)
              : false,
          step,
        };
      }

      return {
        isActive,
        isSemi:
          currentEdbStep === -1
            ? false
            : (hasProduct || hasLeftProduct) && isActive
            ? true
            : isActive
            ? !fixed.every(
                (z) => (z[z.length - 1] as number) >= step || z.includes(step)
              )
            : false,
        step,
      };
    });
    return timeline;
  };
  const getRecenteDate = () => {
    if (step <= 0) return edb?.createdAt || null;
    const opsModified = (activeOps || [])
      .map((op: any) => ({
        ...op,
        statusHistory:
          step === 5
            ? (op?.statusHistory || []).find((h: any) => h.status === 6) ||
              (op?.statusHistory || []).find((h: any) => h.status === 5)
            : (op?.statusHistory || []).find((h: any) => h.status === step),
      }))
      .filter((op: any) => op.statusHistory !== undefined)
      .sort(
        (a: any, b: any) =>
          new Date(b.statusHistory?.statusDate).getTime() -
          new Date(a.statusHistory?.statusDate).getTime()
      );
    if (opsModified.length) {
      return opsModified[0]?.statusHistory?.statusDate || null;
    }
    return null;
  };
  const setConfig = getTimeLine().find((x) => x.step === step);

  useEffect(() => {
    if (activeOps.length > 0 && step > 0) {
      if (currentEdbStep === step) {
        setProgressBar(50);
      }
      if (currentEdbStep < step) {
        // setIsActive(false);
        setProgressBar(0);
      }
      if (currentEdbStep > step) {
        setProgressBar(100);
      }
    } else if (step <= 0) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      step === currentEdbStep
        ? setProgressBar(50)
        : step > currentEdbStep
        ? setProgressBar(0)
        : setProgressBar(100);
    }
  }, [currentEdbStep, activeOps, step]);

  const recentDate = getRecenteDate();
  return (
    <div className={classes.step}>
      <div className={classes.stepLabel}>
        <div
          className={`${classes.step} step ${
            setConfig?.isActive ? "active" : ""
          } ${setConfig?.isSemi ? "semi" : ""} ${isRed ? "red" : ""}`}
        />
        <div className={classes.stepTextContainer}>
          <Text
            className={`${classes.stepText} ${
              setConfig?.isActive || setConfig?.isSemi ? "active" : ""
            }`}
          >
            {label}
          </Text>
          <br />
          <Text className={`${classes.stepText}`}>
            {recentDate
              ? dateFormat(new Date(recentDate), DEFAULT_DATE_FORMAT)
              : ""}
          </Text>
        </div>
      </div>
      <div className={`progress-bar ${classes.progressBar}`}>
        <LinearProgress variant="buffer" value={progressBar} valueBuffer={0} />
      </div>
    </div>
  );
}

const useStyles = createUseStyles(() => ({
  stepLabel: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minWidth: 110,
  },
  step: {
    display: "flex",
    // alignItems: 'center',
    "&:last-child .progress-bar": {
      display: "none",
    },
    "& .MuiLinearProgress-root": {
      height: 2,
    },
    "& .MuiLinearProgress-dashed": {
      animation: "none",
    },
    "& .MuiLinearProgress-barColorPrimary": {
      backgroundColor: `${theme.color.blueLight}`,
    },
    "& .MuiLinearProgress-dashedColorPrimary": {
      backgroundSize: "6px 10px",
      backgroundImage:
        "radial-gradient(rgb(204 205 208) 0%, rgb(204 205 208) 16%, transparent 42%)",
    },
  },
  stepText: {
    color: `${theme.color.gray5}`,
    fontSize: 12,
    "&.active": {
      color: `${theme.color.blueLight}`,
      fontWeight: "bold",
    },
  },
  stepTextContainer: {
    textAlign: "center",
    marginTop: 6,
  },
  progressBar: {
    width: 100,
    margin: "15px 0",
  },
  "@media only screen and (min-width: 1024px) and (max-width: 1576px)": {
    progressBar: {
      width: 88,
    },
  },
}));

export default Step;
